import { acceptHMRUpdate, defineStore } from 'pinia';
import { plainToInstance } from 'class-transformer';
import path from '~/utilities/path';
import pyscriptApi from '~/utilities/pyscript-api';
import type { PsdcApiError } from '~/utilities/pyscript-api-models';
import { FileMetadata } from '~/utilities/pyscript-api-models';
import { buildFileTree } from '~/utilities/build-file-tree';
import type { TreeNode } from '~/utilities/build-file-tree';
import { createFormDataBlob } from '~/utilities/create-form-data-blob';
import { FILE_IGNORE_LIST, MAX_FILE_SIZE, MAX_FILE_SIZE_TEXT } from '~/utilities/constants';

export interface FileContent {
  content: string;
  contentType: string;
}

/**
 * Type of nodes in the `vfsStore.fileTree` structure.
 */
export interface VfsNode {
  label: string;
  type: 'file' | 'folder';
  children?: VfsNode[];
}

/**
 * Virtual File System Store.
 *
 * Everything in this store revolves around a single project. If the state's
 * `projectId` changes, the files and all the store's data change as well.
 *
 * IMPORTANT NOTE:
 * When creating/renaming/deleting files or folders in S3 via the PyScript API
 * the calls are not awaited as that can cause a lag to the user.
 * To address this lag, the file is deleted from the store's state first, with
 * the assumption that the API call will succeed. However, if the API does fail,
 * the original file is re-added in the store's state.
 */
export const useVfsStore = defineStore('vfs', {
  state: () => ({
    /** Determines if we're loading the project's file list data. */
    loading: false,

    /**
     * Used for all PSDC API requests to determine which project to CRUD files
     * and folders to.
     */
    projectId: '',

    /** Project version */
    version: '',

    /**
     * A Map of all the files contained in a project.
     */
    fileListMap: new Map<FileMetadata['path'], FileMetadata>(),

    /**
     * Store the contents of files content. It represents the file's that are
     * opened in an editor tab. If a key is in the editorTabs, it should also
     * be here. The value may be undefined if the file is still loading.
     *
     * The key represents the path relative to the project's version path, not
     * the full S3 bucket path.
     * E.g. `index.html` instead of `user-id/project-id/version/index.html`.
     */
    fileContentMap: new Map<string, FileContent | null>(),

    /** Keeps track of files with unsaved changes */
    unsavedFilesMap: new Map<string, boolean>(),

    /**
     * List of relative file paths used to determine the tabs and their order
     * in the editor pane. Keys in this array should always be present in /
     * `state.fileContentMap`.
     *
     * The reason this state is necessary is two-fold. 1) Since tabs can be
     * reordered, we need to keep track of that order of that order. 2) When we
     * add the ability to have multiple panes, it's possible for the same file
     * to be open in multiple panes.
     */
    editorTabs: [] as string[],

    /** File path of the active file. The path is relative to the project's version. */
    activeFilePath: '',

    newFileField: {
      /**
       * Should not begin with a slash. An empty string would display the
       * new file field at the project's file explorer root (i.e. relative
       * to the version directory).
       *
       * For example, `path/to/some/dir` would be the directory where the
       * new file field appears.
       */
      dirname: '',
      show: false,
    },

    newFolderField: {
      /**
       * Should not begin with a slash. An empty string would display the
       * new folder field at the project's file explorer root (i.e. relative
       * to the version directory).
       *
       * For example, `path/to/some/dir` would be the directory where the
       * new directory field appears.
       */
      dirname: '',
      show: false,
    },

    /**
     * Updated when a file or folder is in a renaming state.
     */
    isRenaming: false,

    /**
     * Used to determine if we should create the .zip file to export the
     * project so it can be used as a library.
     */
    export: false,
  }),

  getters: {
    /**
     * Creates a complete tree of subfolders and files.
     */
    fileTree(): TreeNode[] {
      const fileListArray = Array.from(this.fileListMap, ([_path, metadata]) => metadata);
      return buildFileTree(fileListArray);
    },

    /**
     * @example {userId}/{projectId}/{version}/
     */
    bucketBasePath(): string {
      if (this.fileListMap.size === 0) return '';

      const { userId, projectId, version } = this.fileListMap.values().next().value;
      return `${userId}/${projectId}/${version}/`;
    },

    /**
     * Used to improve the UX when editing a file/folder name by adding an
     * opacity to all other file/folders in the tree. This can occur when
     * creating or renaming files and folders.
     */
    isEditingFileOrFolderName(): boolean {
      return this.newFileField.show || this.newFolderField.show || this.isRenaming;
    },
  },

  actions: {
    /** Initialize the state. */
    async initVfs(projectId: string, version?: string) {
      this.projectId = projectId;
      this.version = version || 'latest';

      this.loading = true;
      // Fetch our list of files and convert it to a Map
      await this.fetchFileList();

      // Check if we should export this project
      await this.shouldExportProject();
      this.loading = false;
    },

    /** Fetch our list of files and convert it to a Map */
    async fetchFileList() {
      const list = await pyscriptApi.listFiles(this.projectId, this.version);

      if (!list.length) return;

      const mappedArray: [string, FileMetadata][] = list.map((item) => [item.path, item]);
      this.fileListMap = new Map(mappedArray);
    },

    async shouldExportProject() {
      // Get the configuration file from fileListMap, this can
      // be a toml or json file.
      const project = await pyscriptApi.getProject(this.projectId);
      this.export = project.export;
    },

    /** Reset the state. */
    resetVfs() {
      this.$reset();
    },

    /**
     * Checks if a file or folder path has a conflict with an existing file or folder.
     * E.g. Creating a folder `path/to/some/.vscode/`  conflicts with the file `path/to/some/.vscode`.
     * E.g. Renaming a folder `path/to/old-folder/` to `path/to/new-folder/` conflicts if a file or
     * folder named `path/to/new-folder/`.
     *
     * @param path Path of the file or folder. E.g. `path/to/some/file.txt` or `path/to/folder/`.
     */
    hasConflict(path: string): boolean {
      const isFolder = path.endsWith('/');
      const allPaths = Array.from(this.fileListMap.keys(), this.convertPathToString);
      const pathString = this.convertPathToString(path);
      return allPaths.some((p) => (isFolder ? p.startsWith(pathString) : p === pathString));
    },

    /**
     * Creates restore point in case the API request fails.
     *
     * @returns
     * A function that restores the state to the point when the function was called.
     */
    createRestorePoint() {
      const backup = {
        fileListMap: new Map(this.fileListMap),
        fileContentMap: new Map(this.fileContentMap),
        unsavedFilesMap: new Map(this.unsavedFilesMap),
        editorTabs: [...this.editorTabs],
        activeFilePath: this.activeFilePath,
      };

      return () => {
        this.fileListMap = backup.fileListMap;
        this.fileContentMap = backup.fileContentMap;
        this.unsavedFilesMap = backup.unsavedFilesMap;
        this.editorTabs = backup.editorTabs;
        this.activeFilePath = backup.activeFilePath;
      };
    },

    /**
     * Converts a path to a string.
     *
     * Use this to compare pahts. DON'T USE something like `.startsWith()` as
     * that can lead to false positives.
     *
     * @param filePath Path of the file to create. E.g. `path/to/some/file.txt`.
     * @returns A string representation of the path. E.g. `path,to,some,file.txt`.
     */
    convertPathToString(filePath: string) {
      return filePath.split('/').toString();
    },

    /**
     * Closes a tab in the editor pane by removing it from the store state's
     * `fileContentMap`, `unsavedFilesMap`, `editorTabs`, and `activeFilePath`.
     *
     * @param path Path of the file to close. E.g. `path/to/some/file.txt`.
     */
    closeTab(path: string) {
      const index = this.editorTabs.indexOf(path);
      if (index > -1) {
        this.editorTabs.splice(index, 1);
      }

      this.fileContentMap.delete(path);
      this.unsavedFilesMap.delete(path);

      if (this.activeFilePath === path) {
        this.activeFilePath = '';
      }
    },

    /**
     * Opens a tab in the editor pane by adding it to the store state's
     * `fileContentMap`, `editorTabs`, and `activeFilePath`.
     *
     * @param path Path of the file to close. E.g. `path/to/some/file.txt`.
     */
    async openTab(path: string) {
      if (!path || !this.fileListMap.has(path)) return;
      // Set the active file path.
      this.activeFilePath = path;

      // Open the tab if it's not already open.
      if (!this.editorTabs.includes(path)) {
        this.editorTabs.push(path);
      }

      // Fetch the file content if it's not already loaded.
      if (!this.fileContentMap.has(path)) {
        // Set the path to null to indicate it's loading.
        this.fileContentMap.set(path, null);
        await this.fetchFileContent(path);
      }
    },

    /** Updates a tab path. Used for when a rename or move occurs. */
    patchTab(oldPath: string, newPath: string) {
      const index = this.editorTabs.indexOf(oldPath);

      if (index > -1) {
        this.editorTabs[index] = newPath;
      }
    },

    /**
     * Updates a file's path and metadata in the store's state.
     *
     * This can occur when renaming, replacing, or moving files.
     */
    patchFileListPathAndMetadata(oldPath: string, newPath: string, fileMetadata: FileMetadata) {
      if (!this.fileListMap.has(oldPath)) {
        throw new Error(`The file "${oldPath}" does not exist.`);
      }

      this.fileListMap.set(newPath, fileMetadata);
      this.fileListMap.delete(oldPath);
    },

    /**
     * Updates a file's path and content and metadata in the store's state.
     *
     * This can occur when renaming, replacing, or moving files.
     */
    patchFileContentPath(oldPath: string, newPath: string) {
      // Only need to update the file content if it was already previously loaded.
      const oldFileContent = this.fileContentMap.get(oldPath);
      if (oldFileContent) {
        this.fileContentMap.set(newPath, oldFileContent);
        this.fileContentMap.delete(oldPath);
      }
    },

    /**
     * When the active file's path to the new path when a file is renamed or moved.
     */
    patchActiveFilePath(oldPath: string, newPath: string) {
      // Update the active file path if it was previously active.
      if (this.activeFilePath === oldPath) {
        this.activeFilePath = newPath;
      }
    },

    /**
     * When the active file's path to the new path only when it's renamed. This
     * doesn't occur when a file is moved because the file is saved prior to
     * it moving.
     */
    patchUnsavedFilesMap(oldPath: string, newPath: string) {
      const value = this.unsavedFilesMap.get(oldPath);
      if (value) {
        this.unsavedFilesMap.set(newPath, value);
        this.unsavedFilesMap.delete(oldPath);
      }
    },

    /**
     * Adds a file path to the touched file paths, used to detect if a file has
     * unsaved changes.
     */
    addTouchedFilePath(path: string) {
      this.unsavedFilesMap.set(path, true);
    },

    /**
     * Fetches the content of a file and stores it.
     *
     * @param path Path of the file to fetch.
     */
    async fetchFileContent(path: string) {
      if (!path) throw new Error('The path is required to fetch a file.');

      // We've already fetched this file before so no need to refetch
      if (this.fileContentMap.get(path)?.content) {
        return this.fileContentMap.get(path)?.content;
      }

      const resp = await pyscriptApi.getFileContent(this.projectId, this.version, path);
      const contentType = resp.headers.get('content-type');
      let content: string | undefined;

      if (!resp.ok) {
        throw new Error(`Failed to fetch file content for "${path}".`);
      }

      if (
        path.endsWith('.webp') ||
        contentType?.includes('image/') ||
        contentType?.includes('video/') ||
        contentType?.includes('audio/') ||
        contentType === 'application/pdf'
      ) {
        content = await resp.blob().then((blob) => {
          return new Promise<string>((resolve, reject) => {
            try {
              const reader = new FileReader();
              reader.onload = function () {
                resolve(this.result as string);
              };
              reader.readAsDataURL(blob);
            } catch (e) {
              reject(e);
            }
          });
        });
      } else {
        content = await resp.text();
      }

      this.fileContentMap.set(path, {
        content,
        contentType,
      } as FileContent);

      return content;
    },

    /**
     * Downloads a file.
     *
     * @param filePath Path of the file. E.g. `path/to/some/file.txt`
     */
    async downloadFile(filePath: string) {
      const fileName = path.basename(filePath);
      const downloadUrl = this.fileListMap.get(filePath)?.url;

      if (!filePath || !downloadUrl) {
        // eslint-disable-next-line no-alert
        alert('Failed to download');
        return;
      }

      // eslint-disable-next-line no-alert
      const confirmResult = confirm(`Are you sure you want to download "${fileName}"?`);
      if (!confirmResult) {
        return;
      }

      const resp = await pyscriptApi.getFileContent(this.projectId, this.version, filePath);
      const blob = await resp.blob();

      // Create a link in memory and use it to download the file.
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = fileName;
      link.click();
    },

    /**
     * Duplicates a file.
     *
     * @param filePath Path of the file. E.g. `path/to/some/file.txt`
     */
    async duplicateFile(filePath: string) {
      // Get the full path of the file.
      const fullPath = this.fileListMap.get(filePath)!.path;

      // Fetch the blob contents of a file, which can be an image, video, audio, text, etc.
      const resp = await pyscriptApi.getFileContent(this.projectId, this.version, fullPath);
      const contentType = resp.headers.get('content-type');
      const blob = await resp.blob();

      if (!blob && blob !== '') {
        throw new Error(`Failed to duplicate the file "${filePath}".`);
      }

      // Update the path to append `-copy` to the file's name.
      const parsedFilePath = path.parse(filePath);
      const newFilePath = path.join(
        parsedFilePath.dir,
        `${parsedFilePath.name}-copy${parsedFilePath.ext}`,
      );

      // Create a File object.
      const file = new File([blob], newFilePath, { type: contentType! });

      // Upload the file.
      this.uploadFile(file);
    },

    /**
     * Creates a new empty file.
     *
     * @param filePath Path of the file to create. E.g. `path/to/some/file.txt`.
     */
    async createFile(filePath: string, fileContent: string = ''): Promise<FileMetadata> {
      // Short circuit if the new file path already exists as a file or folder.
      if (this.hasConflict(filePath)) {
        const error: PsdcApiError = {
          code: 'CONFLICT',
          message: `A file or folder "${filePath}" already exists at this location. Please choose a different name.`,
          status: 409,
        };
        throw error;
      }

      const formData = createFormDataBlob(filePath, fileContent);
      const uploadedFileMetadata = await pyscriptApi.uploadFile(this.projectId, formData);
      this.fileListMap.set(filePath, uploadedFileMetadata);
      return uploadedFileMetadata;
    },

    /**
     * Creates a new empty directory.
     *
     * NOTE: Don't pass a trailing slash.
     * @param folderPath Name of the folder to create. E.g. `path/to/some/folder`.
     */
    async createFolder(folderPath: string): Promise<FileMetadata> {
      // Short circuit if the new file path already exists as a file or folder.
      if (this.hasConflict(`${folderPath}/`) || this.hasConflict(folderPath)) {
        const error: PsdcApiError = {
          code: 'CONFLICT',
          message: `A file or folder "${folderPath}" already exists at this location. Please choose a different name.`,
          status: 409,
        };
        throw error;
      }

      const uploadedFileMetadata = await pyscriptApi.createFolder(this.projectId, folderPath);
      this.fileListMap.set(uploadedFileMetadata.path, uploadedFileMetadata);
      return uploadedFileMetadata;
    },

    /**
     * Deletes a file in our store's state, then from S3 via PSDC API.
     * If the API request fails, the file is re-added to the store's state.
     *
     * @param filePathToDelete Path of the file to delete. E.g. `path/to/file.txt`.
     */
    async deleteFile(filePathToDelete: string) {
      /* Optimistically update the UI by removing the file from the state. */
      const restore = this.createRestorePoint();

      // Delete the path from `fileContentMap`, `unsavedFilesMap`, `editorTabs`, and `activeFilePath`.
      this.closeTab(filePathToDelete);

      // Delete the path from `fileListMap`.
      this.fileListMap.delete(filePathToDelete);

      /* Make the API call to delete the file from S3. If an error occurs, restore the state. */
      return pyscriptApi.deleteFile(this.projectId, filePathToDelete).catch((err) => {
        restore();

        // Throw the error to be handled by the caller.
        throw err;
      });
    },

    /**
     * Deletes a folder and all of its contents, as well as removing its files
     * from our state.
     *
     * @param folderPath Path of the folder to delete. E.g. `path/to/folder/`.
     */
    async deleteFolder(folderPath: string) {
      /* Optimistically update the UI. */
      const restore = this.createRestorePoint();
      const folderToDeleteAsString = this.convertPathToString(folderPath);

      // Iterate through all the files in the store's state to detect which
      // files are in the folder being deleted. If detected, delete the file
      // from all Map objects in the store's state.
      Array.from(this.fileListMap).forEach(([keyPath, _valueMetadata]) => {
        const keyPathAsString = this.convertPathToString(keyPath);

        // If the `keyPath` starts with the folder path being deleted, delete it.
        if (keyPathAsString.startsWith(folderToDeleteAsString)) {
          // 1. Delete the file from `fileListMap`.
          this.fileListMap.delete(keyPath);

          // 2. Delete the file from `fileContentMap`.
          this.fileContentMap.delete(keyPath);

          // 3. Delete the file from `unsavedFilesMap`.
          this.unsavedFilesMap.delete(keyPath);
        }
      });

      // 4. If the active file is in the deleted folder, remove it.
      if (this.activeFilePath.startsWith(folderPath)) {
        this.activeFilePath = '';
      }

      // 5. Remove open tabs that are in the deleted folder.
      this.editorTabs = this.editorTabs.filter((filePath) => !filePath.startsWith(folderPath));

      return pyscriptApi.deleteFolder(this.projectId, folderPath).catch((err) => {
        // If an error occours, restore the state.
        restore();

        // Throw the error to be handled by the caller.
        throw err;
      });
    },

    /**
     * Moves a file from one location to another.
     *
     * @param pathFrom Path of the file to move. E.g. `old/path/to/file.txt`
     * @param pathTo Path of the destination. E.g. `new/path/file.txt`
     * @param skipOverwriteConfirmation If true, skips the overwrite confirmation prompt.
     */
    async moveFile(pathFrom: string, pathTo: string, skipOverwriteConfirmation = false) {
      // Short circuit if the paths are the same
      if (pathFrom === pathTo) return;

      let shouldOverwrite = skipOverwriteConfirmation;
      const hasFile = this.fileListMap.has(pathTo);

      // TODO: Show this prompt in a toast message or custom confirmation modal.
      // Confirm if user wants to overwrite the file if it already exists.
      if (hasFile && !skipOverwriteConfirmation) {
        const fileName = path.basename(pathTo);

        // eslint-disable-next-line no-alert
        shouldOverwrite = confirm(
          `"${fileName}" already exists in this destination. Do you want to overwrite it?`,
        );

        if (!shouldOverwrite) {
          return;
        }
      }

      const pathFromMetadata = this.fileListMap.get(pathFrom);

      if (!pathFromMetadata) {
        throw new Error(`The file "${pathFrom}" does not exist.`);
      }

      // 1) Set as a ghost file to indicate to the user it's being moved.
      pathFromMetadata.isGhostFile = true;

      // 2) Fetch the file contents from S3 if not already loaded.
      const resp = await pyscriptApi.getFileContent(this.projectId, this.version, pathFrom);
      const blob = await resp.blob();

      // 3) Upload the file to the new location.const formData = new FormData();
      const formData = new FormData();
      formData.append('file', blob, pathTo);
      const uploadedFileMetadata =
        shouldOverwrite && hasFile
          ? await pyscriptApi.updateFile(this.projectId, formData)
          : await pyscriptApi.uploadFile(this.projectId, formData);

      // 4) Delete the file from the old location.
      await pyscriptApi.deleteFile(this.projectId, pathFrom);

      // 5) Update the store's state.
      this.patchFileListPathAndMetadata(pathFrom, pathTo, uploadedFileMetadata);
      this.patchTab(pathFrom, pathTo);
      this.patchFileContentPath(pathFrom, pathTo);
      this.patchActiveFilePath(pathFrom, pathTo);
      this.patchUnsavedFilesMap(pathFrom, pathTo);
    },

    /**
     * Moves a folder from one location to another.
     *
     * The paths should never start with a slash and should always end with a slash.
     * If it's the root directory, the path will be an empty string.
     *
     * @param pathFrom Path of the folder to move. E.g. `old/path/cool-folder/`
     * @param pathTo Path of the destination. E.g. `some/new/path/`
     */
    async moveFolder(pathFrom: string, pathTo: string) {
      // Short circuit if the paths are the same
      if (pathFrom === pathTo) return;

      // Short circuit if the `pathFrom` is being dropped into its parent.
      // E.g. `some/new/path/cool-folder/` into `some/new/path/`.
      let pathFromParentPath = path.dirname(pathFrom.slice(0, -1));
      pathFromParentPath = pathFromParentPath === '' ? '' : `${pathFromParentPath}/`;
      if (pathFromParentPath === pathTo) return;

      const folderName = pathFrom.split('/').filter(Boolean).at(-1) || '';
      const fileListArray = Array.from(this.fileListMap, ([_key, value]) => value);

      // Destination path including the folder name. E.g. `some/new/path/cool-folder`
      const pathToFolder = path.join(pathTo, folderName);

      const hasFolderConflict = fileListArray.some((file) => file.path.startsWith(pathToFolder));

      let confirmResult: boolean;

      if (hasFolderConflict) {
        // eslint-disable-next-line no-alert
        confirmResult = confirm(
          `A folder named "${folderName}" already exists at this destination. Do you want to merge the two?`,
        );

        if (!confirmResult) {
          return;
        }
      }

      // Get all the file paths in the folder being moved.
      const pathsInFolder = fileListArray
        .filter((file) => file.path.startsWith(pathFrom))
        .map((file) => file.path);

      pathsInFolder.forEach(async (filePathFrom) => {
        // Construct the destination path for each file/folder in the folder being moved.
        const newPathTo = path.join(pathToFolder, filePathFrom.replace(pathFrom, ''));

        if (filePathFrom.endsWith('/')) {
          // Create the folder in the new location by creating a file with a slash at the end.
          this.moveFile(filePathFrom, `${newPathTo}/`, confirmResult);
        } else {
          this.moveFile(filePathFrom, newPathTo, confirmResult);
        }
      });
    },

    /**
     * Rename a file in the store state and S3 via PSDC API.
     *
     * @param oldPath Path of the folder to rename. E.g. `path/to/old-name.txt`
     * @param newPath Path of the destination. E.g. `path/to/new-name.txt`
     */
    async renameFile(oldPath: string, newPath: string) {
      // Short circuit if the new file path already exists.
      if (this.hasConflict(newPath)) {
        const error: PsdcApiError = {
          code: 'CONFLICT',
          message: `A file or folder "${newPath}" already exists at this location. Please choose a different name.`,
          status: 409,
        };
        throw error;
      }

      /* Optimistically update the UI. */

      const restore = this.createRestorePoint();

      // Update the metadata with the new path.
      // Doing this optimistically prior to the API request to prevent a lag and give the feeling of a faster UI.
      const newBasename = path.basename(newPath);
      const oldMetadata = this.fileListMap.get(oldPath) as FileMetadata;
      const newMetadata = plainToInstance(FileMetadata, {
        ...oldMetadata,
        path: path.join(oldMetadata.parsedPath.dir, newBasename), // Full S3 bucket path
        name: newBasename,
      });

      // Update the store's state.
      this.patchFileListPathAndMetadata(oldPath, newPath, newMetadata);
      this.patchTab(oldPath, newPath);
      this.patchFileContentPath(oldPath, newPath);
      this.patchActiveFilePath(oldPath, newPath);

      // PSDC API request to rename the file in S3
      // Intentionally not awaiting to not cause a lag.
      return pyscriptApi.renameFileOrFolder(this.projectId, oldPath, newPath).catch((err) => {
        // If an error occours, restore the state.
        restore();

        // Throw the error to be handled by the caller.
        throw err;
      });
    },

    /**
     * Rename a folder in the store state and S3 via PSDC API.
     *
     * @param oldPath Path of the folder to rename. E.g. `path/to/old-folder/`
     * @param newPath Path of the destination. E.g. `path/to/new-folder/`
     */
    async renameFolder(oldPath: string, newPath: string) {
      // Short circuit if no changes were made.
      if (oldPath === newPath) return;

      // Throw error if the new file path already exists.
      if (this.hasConflict(newPath)) {
        const error: PsdcApiError = {
          code: 'CONFLICT',
          message: `A folder already exists at "${newPath}".`,
          status: 409,
        };
        throw error;
      }

      /* Optimistically update the UI. */

      // Create restore point in case the API request fails.
      const restore = this.createRestorePoint();
      const oldPathAsString = this.convertPathToString(oldPath);

      // NOTE: Don't use Map.prototype.forEach() here or else we'll end up in
      // an infinite loop due to setting and deleting keys in the same loop.
      Array.from(this.fileListMap).forEach(([keyPath, valueMetadata]) => {
        const keyPathAsString = this.convertPathToString(keyPath);

        // Replace the path in all our state's Map objects with the new path.
        // E.g. `some/path/old-folder/index.html` -> `some/path/new-folder/index.html`
        if (keyPathAsString.startsWith(oldPathAsString)) {
          const newFilePath = keyPath.replace(oldPath, newPath);

          // 1. Update the keys of `fileListMap` that contain the old path.
          const newMetadata = plainToInstance(FileMetadata, {
            ...valueMetadata,
            path: valueMetadata.path.replace(oldPath, newPath), // Update the full S3 bucket path.
          });
          this.fileListMap.set(newFilePath, newMetadata);
          this.fileListMap.delete(keyPath);

          // 2. Update the keys of `fileContentMap` that contain the old path.
          if (this.fileContentMap.has(keyPath)) {
            this.fileContentMap.set(newFilePath, this.fileContentMap.get(keyPath) as FileContent);
            this.fileContentMap.delete(keyPath);
          }

          // 3. Update the keys of `unsavedFilesMap` that contain the old path.
          if (this.unsavedFilesMap.has(keyPath)) {
            this.unsavedFilesMap.set(newFilePath, this.unsavedFilesMap.get(keyPath) as boolean);
            this.unsavedFilesMap.delete(keyPath);
          }
        }
      });

      // 4. If the active file is in the renamed folder, update its path.
      if (this.activeFilePath.startsWith(oldPath)) {
        this.activeFilePath = this.activeFilePath.replace(oldPath, newPath);
      }

      // 5. Update all open tabs that contain the old folder path.
      this.editorTabs = this.editorTabs.map((filePath) =>
        filePath.startsWith(oldPath) ? filePath.replace(oldPath, newPath) : filePath,
      );

      const oldPathWithoutTrailingSlash = oldPath.slice(0, -1);
      const newPathWithoutTrailingSlash = newPath.slice(0, -1);

      return pyscriptApi
        .renameFileOrFolder(
          this.projectId,
          oldPathWithoutTrailingSlash,
          newPathWithoutTrailingSlash,
        )
        .catch((err) => {
          // If an error occours, restore the state.
          restore();

          // Throw the error to be handled by the caller.
          throw err;
        });
    },

    /**
     * Save a file.
     *
     * @param filePath Path of the file to save. E.g. `path/to/some/file.txt`.
     */
    async saveFile(filePath: string) {
      const content = this.fileContentMap.get(filePath)?.content;
      if (!content) throw new Error('Unable to save file.');

      // Save the file
      const response = await pyscriptApi.updateFile(
        this.projectId,
        createFormDataBlob(filePath, content),
      );

      // Remove the file from the unsaved files list.
      this.unsavedFilesMap.delete(filePath);

      return response;
    },

    /**
     * Handles files to be uploaded to S3 via PSDC API.
     * This may occur via drag-andpdrop or the file input.
     *
     * The `File.name` should be the path without a leading slash.
     * E.g. `path/to/file.txt`
     */
    async uploadFile(file: File) {
      const basename = path.basename(file.name);

      if (file.size > MAX_FILE_SIZE) {
        // eslint-disable-next-line no-alert
        alert(`UPLOAD ERROR: ${file.name} exceeds max size (${MAX_FILE_SIZE_TEXT})`);
        return;
      }

      if (FILE_IGNORE_LIST.includes(basename)) {
        return;
      }

      // Create a temporary file metadata that will be replaced once the file is uploaded.
      const tempFileMetadata = plainToInstance(FileMetadata, {
        hash: '',
        // Need to add a fake path to accurately display the file in the tree.
        path: file.name,
        name: basename,
        size: file.size,
        updated_at: '',
        // A flag to indicate the file is in the process of being uploaded.
        isGhostFile: true,
      });

      const hasFile = this.fileListMap.has(file.name);
      let shouldOverwrite = false;

      // Confirm if user wants to overwrite the file if it already exists.
      if (hasFile) {
        // eslint-disable-next-line no-alert
        shouldOverwrite = confirm(
          `"${file.name}" already exists in this destination. Do you want to overwrite it?`,
        );

        if (!shouldOverwrite) {
          return;
        }
      }

      // Create a ghost file in the file tree while the file is uploading.
      this.fileListMap.set(file.name, tempFileMetadata);

      // Create the form data.
      const formData = new FormData();
      formData.append('file', file, file.name);

      // Upload the file, then replace the ghost file with the file metadata.
      const fileMetadata =
        hasFile && shouldOverwrite
          ? await pyscriptApi.updateFile(this.projectId, formData)
          : await pyscriptApi.uploadFile(this.projectId, formData);

      this.fileListMap.set(file.name, fileMetadata);

      // If the file was already opened in a tab, replace it with the uploaded file's content.
      if (this.fileContentMap.has(file.name)) {
        this.fileContentMap.delete(file.name);
        this.fetchFileContent(file.name);
      }
    },

    /**
     * Used to determine at which directory in our tree we should display the
     * input field for the user to enter the new file's name before it's created
     * and uploaded to S3.
     */
    updateNewFileField({ dirname, show }: { dirname: string; show: boolean }): void {
      if (typeof dirname === 'string') {
        this.newFileField.dirname = dirname;
      }

      if (typeof show === 'boolean') {
        this.newFileField.show = show;
      }
    },

    /**
     * Used to determine at which directory in our tree we should display the
     * input field for the user to enter the new folders's name before it's
     * created and uploaded to S3.
     */
    updateNewFolderField({ dirname, show }: { dirname: string; show: boolean }): void {
      if (typeof dirname === 'string') {
        this.newFolderField.dirname = dirname;
      }

      if (typeof show === 'boolean') {
        this.newFolderField.show = show;
      }
    },
  },
});

if (import.meta.hot) import.meta.hot.accept(acceptHMRUpdate(useVfsStore, import.meta.hot));
